import { useMemo } from 'react';

import type Big from 'big.js';
import keyBy from 'lodash/keyBy';

import {
  useClusters,
  isClusterManaged,
} from '@cast/shared-cluster-context-data-access';
import {
  getMonthlyCostFromSummary,
  getMonthlySavingsFromOptimization,
} from '@cast/features-cluster-shared';
import {
  AgentStatus,
  ClusterOptimization,
  Cluster,
  ClusterStatus,
  CloudDiscoveryTags,
} from '@cast/types';
import { toNumOrZero, toBig, makeRefetchOnlyFailed } from '@cast/utils';

import { useClusterOptimizationsQuery } from 'hooks/queries/cluster';
import { useClustersSummary } from 'hooks/queries/cost';

import { getPercentage } from '../../SummarySection/utils';
import {
  ClusterRowModel,
  ClusterRowDisplayMetrics,
  SummarySectionMetrics,
  IssuesDrawerMetrics,
} from '../../types';
import { getEfficiencyPercent, toCalcMemoryUnit } from '../../utils';

import { CostReportClusterSummaryNormalized } from '@cast/shared-cost-data-access';

type EnhancedClusterSummary = CostReportClusterSummaryNormalized & {
  gpuIdleTotal: Big;
  gpuNotUsedTotal: Big;
};

type ToDisplayMetricsArgs = {
  optimization?: ClusterOptimization;
  summary?: EnhancedClusterSummary;
};

export const toDisplayMetrics = ({
  optimization,
  summary,
}: ToDisplayMetricsArgs): ClusterRowDisplayMetrics => {
  const { workloadAutoscaler, infrastructureAutoscaler, efficiency } =
    optimization ?? {};
  const services = {
    workloadOptimization: !!workloadAutoscaler?.enabled,
    infrastructureOptimization: !!infrastructureAutoscaler?.enabled,
  };

  const serviceStates = Object.values(services);

  const provisionedCpu = toNumOrZero(efficiency?.provisionedCpuCores);
  const requestedCpu = toNumOrZero(efficiency?.requestedCpuCores);
  const provisionedMemory = toCalcMemoryUnit({
    amount: toNumOrZero(efficiency?.provisionedMemoryBytes),
    unit: 'Bytes',
  });
  const requestedMemory = toCalcMemoryUnit({
    amount: toNumOrZero(efficiency?.requestedMemoryBytes),
    unit: 'Bytes',
  });

  // Calculate reduce waste metrics
  const downscaleWorkloadCpuCores = toNumOrZero(
    workloadAutoscaler?.downscaleCpuCores
  );
  const downscaleNodeCpuCores = toNumOrZero(
    infrastructureAutoscaler?.downscaleCpuCores
  );
  const cpuReduction = downscaleWorkloadCpuCores + downscaleNodeCpuCores;

  const totalSavings = getMonthlySavingsFromOptimization(optimization);

  const wasteReductionPercentage = getPercentage(cpuReduction, provisionedCpu);

  return {
    score: toNumOrZero(efficiency?.score ?? 0),
    // Old format fields (used by DemoClusterRow)
    efficiency: {
      cpu: {
        provisioned: provisionedCpu,
        requested: requestedCpu,
        efficiencyPercent: getEfficiencyPercent(requestedCpu, provisionedCpu),
      },
      memory: {
        provisioned: provisionedMemory,
        requested: requestedMemory,
        efficiencyPercent: getEfficiencyPercent(
          requestedMemory,
          provisionedMemory
        ),
      },
    },
    reduceWaste: {
      percentage: wasteReductionPercentage,
      cpuCount: cpuReduction,
      optimal: !totalSavings && !wasteReductionPercentage,
      workloadDownscaleCpuCores: downscaleWorkloadCpuCores,
      nodeDownscaleCpuCores: downscaleNodeCpuCores,
    },
    save: totalSavings,
    // New format fields (used by TableSection)
    servicesCount: {
      total: serviceStates.length,
      enabled: serviceStates.filter(Boolean).length,
    },
    services,
    issues:
      toNumOrZero(infrastructureAutoscaler?.issueCount) +
      toNumOrZero(workloadAutoscaler?.issueCount),
    resources: summary
      ? {
          cpu: {
            allocatable: summary.cpuAllocatableTotal.toNumber(),
            used: summary.cpuUsed.toNumber(),
            requested: summary.cpuRequestedTotal.toNumber(),
          },
          memory: {
            allocatable: toCalcMemoryUnit({
              amount: summary.ramAllocatableTotal.toNumber(),
              unit: 'GiB',
            }),
            used: toCalcMemoryUnit({
              amount: summary.ramUsed.toNumber(),
              unit: 'GiB',
            }),
            requested: toCalcMemoryUnit({
              amount: summary.ramRequestedTotal.toNumber(),
              unit: 'GiB',
            }),
          },
          gpu: summary.gpuAllocatableTotal.gt(0)
            ? {
                allocatable: summary.gpuAllocatableTotal.toNumber(),
                used: summary.gpuUsedTotal.toNumber(),
                requested: summary.gpuRequestedTotal.toNumber(),
              }
            : undefined,
        }
      : undefined,
    nodes: summary
      ? {
          total: summary.nodesCountTotal.toNumber(),
          onDemand: summary.nodeCountOnDemand.toNumber(),
          spot: summary.nodeCountSpot.toNumber(),
          spotFallback: summary.nodeCountSpotFallbackCastai.toNumber(),
        }
      : undefined,
    computeCost: summary
      ? {
          monthlyTotal: getMonthlyCostFromSummary(summary) ?? 0,
        }
      : undefined,
  };
};

const toDiscoveredDisplayMetrics = (
  tags?: CloudDiscoveryTags
): ClusterRowDisplayMetrics => {
  if (!tags) {
    return { isLoading: true };
  }

  const cpuTotal = toNumOrZero(tags['cloud-discovery-cpu']);
  const memoryTotal = toCalcMemoryUnit({
    amount: tags['cloud-discovery-memory-mib'],
    unit: 'MiB',
  });
  const nodeCountOnDemand = toNumOrZero(
    tags['cloud-discovery-on-demand-node-count']
  );
  const nodeCountSpot = toNumOrZero(tags['cloud-discovery-spot-node-count']);
  const monthlyCostTotal = toNumOrZero(
    tags['cloud-discovery-total-monthly-cost-usd']
  );

  return {
    resources: {
      cpu: {
        allocatable: cpuTotal,
        used: 0,
        requested: 0,
      },
      memory: {
        allocatable: memoryTotal,
        used: 0,
        requested: 0,
      },
    },
    nodes: {
      total: nodeCountOnDemand + nodeCountSpot,
      onDemand: nodeCountOnDemand,
      spot: nodeCountSpot,
      spotFallback: 0,
    },
    computeCost: monthlyCostTotal
      ? {
          monthlyTotal: monthlyCostTotal,
        }
      : undefined,
  };
};

const toSummarySectionRowItemFromSummary = (
  summary: EnhancedClusterSummary
): SummarySectionMetrics => {
  const currentMonthlyCost = getMonthlyCostFromSummary(summary) ?? 0;
  return {
    requestedCpuCores: summary.cpuRequestedTotal.toNumber(),
    provisionedCpuCores: summary.cpuAllocatableTotal.toNumber(),
    currentMonthlyCost,
    optimizedMonthlyCost: currentMonthlyCost,
  };
};

const toDiscoveredSummaryRowItem = ({
  tags,
}: {
  tags?: CloudDiscoveryTags;
}): SummarySectionMetrics => {
  if (!tags) {
    return {};
  }

  const currentMonthlyCost = toNumOrZero(
    tags['cloud-discovery-total-monthly-cost-usd']
  );

  return {
    provisionedCpuCores: toNumOrZero(tags['cloud-discovery-cpu']),
    currentMonthlyCost,
    optimizedMonthlyCost: currentMonthlyCost,
  };
};

const toSummarySectionRowItem = ({
  optimization,
}: {
  optimization?: ClusterOptimization;
}): SummarySectionMetrics => {
  const { workloadAutoscaler, infrastructureAutoscaler, cost, efficiency } =
    optimization ?? {};
  const totalSavings = getMonthlySavingsFromOptimization(optimization);

  return {
    requestedCpuCores: toNumOrZero(efficiency?.requestedCpuCores),
    usedCpuCores: toNumOrZero(efficiency?.usedCpuCores),
    provisionedCpuCores: toNumOrZero(efficiency?.provisionedCpuCores),
    downscaleWorkloadCpuCores: toNumOrZero(
      workloadAutoscaler?.downscaleCpuCores
    ),
    downscaleNodeCpuCores: toNumOrZero(
      infrastructureAutoscaler?.downscaleCpuCores
    ),
    currentMonthlyCost: toNumOrZero(cost?.totalCostMonthly),
    optimizedMonthlyCost: toNumOrZero(cost?.totalCostMonthly) - totalSavings,
  };
};

export const toIssuesDrawerModel = ({
  workloadAutoscaler,
  infrastructureAutoscaler,
}: ClusterOptimization): IssuesDrawerMetrics => {
  const workloadSavings = toBig(workloadAutoscaler?.memorySavingsMonthly).add(
    toBig(workloadAutoscaler?.cpuSavingsMonthly)
  );
  const nodeSavings = toBig(infrastructureAutoscaler?.memorySavingsMonthly).add(
    toBig(infrastructureAutoscaler?.cpuSavingsMonthly)
  );
  return {
    savingsMonthly: {
      workloadAutoscaler: workloadSavings.toNumber(),
      nodeAutoscaler: nodeSavings.toNumber(),
      total: workloadSavings.add(nodeSavings).toNumber(),
    },
  };
};

type ClusterToRowModelArgs = {
  cluster: Cluster;
  optimization?: ClusterOptimization;
  summary?: EnhancedClusterSummary;
  isLoading: boolean;
};

const clusterToRowModel = ({
  cluster,
  optimization,
  summary,
  isLoading,
}: ClusterToRowModelArgs): ClusterRowModel => {
  const isDiscovered = cluster.status === ClusterStatus.DISCOVERED;
  const tags = cluster.tags;

  if (!optimization) {
    const isDisconnected = cluster.agentStatus === AgentStatus.DISCONNECTED;
    const displayMetrics = isDiscovered
      ? toDiscoveredDisplayMetrics(tags)
      : summary
      ? toDisplayMetrics({ summary })
      : { isLoading: isLoading && !isDisconnected };

    return {
      ...cluster,
      isDiscovered,
      isManaged: isClusterManaged(cluster),
      displayMetrics,
      summaryMetrics: isDiscovered
        ? toDiscoveredSummaryRowItem({ tags })
        : summary
        ? toSummarySectionRowItemFromSummary(summary)
        : {},
      issuesDrawerMetrics: {},
    };
  }

  const displayMetrics = toDisplayMetrics({ optimization, summary });
  return {
    ...cluster,
    isDiscovered,
    isManaged: isClusterManaged(cluster),
    displayMetrics,
    summaryMetrics: toSummarySectionRowItem({ optimization }),
    issuesDrawerMetrics: toIssuesDrawerModel(optimization),
  };
};

export const useCastClusterRowsModels = () => {
  const {
    clusters,
    isLoading: isClustersLoading,
    error,
    refetch,
  } = useClusters({
    polling: true,
    withDiscovered: true,
  });

  const {
    data: clusterOptimizations,
    error: optimizationsError,
    isLoading: optimizationsLoading,
    refetch: refetchOptimizations,
  } = useClusterOptimizationsQuery();

  const {
    summaries,
    error: summariesError,
    isLoading: summariesLoading,
    refetch: refetchSummaries,
  } = useClustersSummary();

  const isLoading =
    isClustersLoading || optimizationsLoading || summariesLoading;

  const isSummaryCardsLoading = isClustersLoading || summariesLoading;

  const metricsLoading = optimizationsLoading || summariesLoading;

  const clusterRowModels = useMemo(() => {
    const optimizationsByClusterId = keyBy(
      clusterOptimizations ?? [],
      'clusterId'
    );
    const summariesByClusterId = keyBy(summaries ?? [], 'clusterId');

    return (
      clusters?.map((cluster) => {
        const optimization = optimizationsByClusterId[cluster.id];
        const summary = summariesByClusterId[cluster.id];
        return clusterToRowModel({
          cluster,
          optimization,
          summary,
          isLoading: metricsLoading,
        });
      }) || []
    );
  }, [clusters, clusterOptimizations, summaries, metricsLoading]);

  return {
    isClustersLoading,
    isLoading,
    isSummaryCardsLoading,
    isOptimizationsLoading: optimizationsLoading,
    ...makeRefetchOnlyFailed(
      [optimizationsError, refetchOptimizations],
      [summariesError, refetchSummaries],
      [error, refetch]
    ),
    clusterRowModels,
  };
};
